import { useAppStorage } from '@solfacil/plasma-utils';
import { jwtDecode } from 'jwt-decode';
import { defineStore } from 'pinia';

import {
  type Partner,
  type Score,
  type User,
} from '~/types/ecommerce/generated.codegen';

import { UserProfile } from '../types/enums';

export const useUserStore = defineStore('user', () => {
  const regionStore = useRegionStore();

  // State
  const realUser = ref<User | undefined>(useAppStorage<User>().get('user'));
  const personifiedUser = ref<Partner | undefined>(
    useAppStorage<Partner>().get('personifiedUser'),
  );
  const inPersonification = ref(
    useAppStorage<boolean>().get('inPersonification', false) || false,
  );

  // Getters
  const user = computed(() => {
    return inPersonification.value ? personifiedUser.value : realUser.value;
  });

  const personifiedUserId = computed(() => {
    return personifiedUser.value?.id || '';
  });

  const partnerId = computed(() => {
    if (inPersonification.value) {
      return personifiedUser.value?.id || '';
    }
    return realUser.value?.partner?.id || '';
  });

  const userRecipient = computed(() => {
    if (inPersonification.value) {
      return personifiedUser.value?.recipient || '';
    }
    return realUser.value?.partner?.recipient || '';
  });

  const userName = computed(() => {
    if (inPersonification.value) {
      return personifiedUser.value?.tradingName || '';
    }
    return realUser.value?.fullName || '';
  });

  const isAdmin = computed(() => {
    const adminUser = realUser.value?.profile === UserProfile.ADMIN;
    return inPersonification.value || adminUser;
  });

  const isWholesaler = computed(() => {
    if (inPersonification.value) {
      return personifiedUser.value?.wholesale?.status || false;
    }
    return realUser.value?.partner?.wholesale?.status || false;
  });

  const wholesalerMinValue = computed(() => {
    if (isWholesaler.value) {
      if (inPersonification.value) {
        return personifiedUser.value?.wholesale?.value || 0;
      }
      return realUser.value?.partner?.wholesale?.value || 0;
    }
    return 0;
  });

  const registrationType = computed(() => {
    if (inPersonification.value) {
      return personifiedUser.value?.registration_type || '';
    }
    return realUser.value?.partner?.registration_type || '';
  });

  const isBasicRegistrationType = computed(() => {
    return registrationType.value === 'basic';
  });

  // Actions
  function setUser(user: User) {
    realUser.value = user;
    useAppStorage<User>().set('user', user);

    if (!isAdmin.value) {
      setInPersonification(false);
      removePersonifiedUser();
    }

    regionStore.setRegionInitialValue(user, inPersonification.value);
  }

  function setInPersonification(value: boolean) {
    inPersonification.value = value;
    useAppStorage<boolean>().set('inPersonification', value);

    // se desligar a personificação, limpa o parceiro
    if (!value) {
      personifiedUser.value = undefined;
      useAppStorage<Partner>().remove('personifiedUser');
    }
  }

  function setPersonifiedUser(user: Partner) {
    personifiedUser.value = user;
    useAppStorage<Partner>().set('personifiedUser', user);
    setInPersonification(true);
    regionStore.setRegionInitialValue(user, true);
  }

  function removePersonifiedUser() {
    personifiedUser.value = undefined;
    setInPersonification(false);
  }

  // Score Solfacil Mais
  const scoreSolfacilPlus = ref<Score | undefined>(
    useAppStorage<Score>().get('scoreSolfacilPlus'),
  );

  function getScoreSolfacilPlus() {
    return scoreSolfacilPlus.value;
  }

  function setScoreSolfacilPlus(score: Score) {
    scoreSolfacilPlus.value = score;
    useAppStorage<Score>().set('scoreSolfacilPlus', score);
  }

  // Permissions
  function getPermissions() {
    const accessToken = useCookie('access_token');
    if (!accessToken.value) return [];

    const decoded = jwtDecode(accessToken.value) as any;
    return decoded.groups || [];
  }
  return {
    // state
    user,
    realUser,
    personifiedUser,
    inPersonification,
    scoreSolfacilPlus,

    // getters
    personifiedUserId,
    partnerId,
    userRecipient,
    userName,
    isAdmin,
    isWholesaler,
    wholesalerMinValue,
    registrationType,
    isBasicRegistrationType,
    // actions
    setUser,
    setInPersonification,
    setPersonifiedUser,
    removePersonifiedUser,
    getScoreSolfacilPlus,
    setScoreSolfacilPlus,
    getPermissions,
  };
});
